import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { ReportsView } from './ReportsView.js';
import { useReportsQuery } from './useReportsQuery.js';

/**
 * The `/reports` route component (D-05 — the one finished room). Reads the typed
 * search params (range/start/end), fires the coarse-endpoint TanStack Query, and
 * renders the faithful 1:1 ReportsView on success. Loading → a page-shaped `RouteSkeleton`;
 * error → the error-state copy with a retry that refetches the query.
 *
 * The server keeps computing the report; this component never re-aggregates.
 */

const routeApi = getRouteApi('/reports');

export function ReportsRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useReportsQuery(search);

  if (query.isPending) {
    return <RouteSkeleton label={t('reports:title')} rows={4} columns={2} action={false} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('reports:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('reports:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('reports:error.retry')}
        </button>
      </section>
    );
  }

  return <ReportsView report={query.data} search={search} />;
}
